<template>
  <b-modal
    ref="modal"
    :title="`Configure ${xourceNames.suricata.extendedName} Alerts`"
    @ok="saveAction"
    @hidden="syncConfig"
  >
    <validation-observer ref="form">
      <validation-provider
        name="Interface Name"
        rules="required"
        v-slot="{ errors }"
      >
        <b-form-group label="Network Interface Name">
          <b-form-input
            placeholder="eth0"
            v-model="config.interfaceName"
            :state="errors[0] ? false : null"
            class="form-control-merge"
          ></b-form-input>
          <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Alert Log Threshold">
          <b-col
            md="6"
            class="p-0"
          >
            <div class="d-flex threshold-range-selector">
              <span><b>Low</b></span>
              <b-form-input
                  v-model="config.severityLevel"
                  class="form-control-merge rotated-input"
                  min="1"
                  max="3"
                  type="range"
                ></b-form-input>
              <span><b>High</b></span>
              <b-icon icon="info-circle" class="h3" variant="dark" id="suricata-info"></b-icon>
            </div>
          </b-col>
          <b-popover target="suricata-info" triggers="hover" placement="bottom">
            <template #title>Setting Alert Thresholds</template>
              <li>
                <b>Low:</b> Generate logs for all alert severity levels. This
                may result in a large number of unecessary alerts. This threshold
                can be used to test whether Suricata is generating alerts properly.
              </li>
              <li>
                <b>Medium (recommended):</b> Generate logs for moderate to high priority alerts.
              </li>
              <li>
                <b>High:</b> Generate logs for only the highest priority alerts. This may result
                in some relevant/actionable alerts being missed.
              </li>
          </b-popover>
        </b-form-group>
        <div class="mt-2">
          <b-link
            v-b-toggle.suricata-advanced
            aria-controls="suricata-advanced"
            class="advanced-toggle"
          >
            Advanced
          </b-link>
          <b-collapse id="suricata-advanced" class="mt-2">
            <validation-provider
              name="Subnets Filter"
              rules="prefixListValid"
              v-slot="{ errors }"
            >
              <b-form-group>
                <template #label>
                  Subnets Filter
                  <b-icon
                    icon="info-circle"
                    class="ml-1 text-muted"
                    v-b-tooltip.hover
                    title="Filter for only relevant traffic. Add one or more subnets separated by commas."
                  />
                </template>
                <b-form-input
                  placeholder="Subnets Filter"
                  v-model="config.subnetsFilter"
                  :state="errors[0] ? false : null"
                  class="form-control-merge"
                ></b-form-input>
                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
            <b-form-group>
              <template #label>
                File Retention Policy
                <b-icon
                  icon="info-circle"
                  class="ml-1 text-muted"
                  v-b-tooltip.hover
                  title="Configure Suricata's file retention policy in duration string (e.g., 5s, 90m, 24h)."
                />
              </template>
              <b-form-row>
                <b-col>
                  <div>Polling Interval</div>
                  <validation-provider
                    name="Polling Interval"
                    rules="goDurationString"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      placeholder="5m"
                      v-model="config.fileRetention.pollingInterval"
                      :state="errors[0] ? false : null"
                      class="form-control-merge"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
                <b-col>
                  <div class="mt-1">Eve Log Retention Period</div>
                  <validation-provider
                    name="Eve Log Retention Period"
                    rules="goDurationString"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      placeholder="90m"
                      v-model="config.fileRetention.eveLogRetentionPeriod"
                      :state="errors[0] ? false : null"
                      class="form-control-merge"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
                <b-col>
                  <div class="mt-1">Pcap Log Retention Period</div>
                  <validation-provider
                    name="Pcap Log Retention Period"
                    rules="goDurationString"
                    v-slot="{ errors }"
                  >
                    <b-form-input
                      placeholder="24h"
                      v-model="config.fileRetention.pcapLogRetentionPeriod"
                      :state="errors[0] ? false : null"
                      class="form-control-merge"
                    ></b-form-input>
                    <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
              </b-form-row>
            </b-form-group>
          </b-collapse>
        </div>
      </validation-provider>
    </validation-observer>
  </b-modal>
</template>

<script>
import configModalMixin from './configModalMixin';

export default {
  mixins: [configModalMixin],
  data() {
    return {
      config: {
        interfaceName: '',
        subnetsFilter: '',
        severityLevel: '',
        xomeName: '',
        fileRetention: {
          pollingInterval: null,
          eveLogRetentionPeriod: null,
          pcapLogRetentionPeriod: null,
        },
      },
    };
  },
  props: {
    xomeName: {
      type: String,
      required: true,
    },
  },
  computed: {
    postConfig() {
      // Deep copy to avoid form errors during type conversions.
      const newConfig = JSON.parse(JSON.stringify(this.config));
      // Convert the textual list of subnets into an array, white-space trimmed and split by commas.
      newConfig.subnetsFilter = newConfig.subnetsFilter.replace(/\s+/g, '');
      newConfig.subnetsFilter = newConfig.subnetsFilter.length === 0
        ? []
        : newConfig.subnetsFilter.split(',');
      newConfig.enabled = true;
      newConfig.severityLevel = Number(newConfig.severityLevel);
      newConfig.xomeName = this.xomeName;
      const { fileRetention: frConfig, ...rest } = newConfig;
      const { fileRetention: _fr, ...base } = this.value;
      const fileRetention = Object.fromEntries(
        Object.entries(frConfig).filter(([, v]) => v != null && v !== ''),
      );
      const result = { ...base, ...rest };
      if (Object.keys(fileRetention).length > 0) {
        result.fileRetention = fileRetention;
      }
      return result;
    },
  },
  methods: {
    /** Sync the form fields `config` data member with the `value` prop holding the original config. */
    syncConfig() {
      const subnetsFilter = this.value.subnetsFilter || [];
      this.config = {
        interfaceName: this.value.interfaceName || '',
        severityLevel: this.value.severityLevel,
        subnetsFilter: subnetsFilter.join(', ') || '',
        fileRetention: {
          pollingInterval: this.value.fileRetention?.pollingInterval ?? null,
          eveLogRetentionPeriod: this.value.fileRetention?.eveLogRetentionPeriod ?? null,
          pcapLogRetentionPeriod: this.value.fileRetention?.pcapLogRetentionPeriod ?? null,
        },
      };
    },
  },
};
</script>
<style scoped>
.threshold-range-selector {
  gap: 1rem;
}

.threshold-range-selector span {
  font-size: 0.9rem;
}

.rotated-input {
  transform: rotate(180deg);
}

.advanced-toggle {
  text-decoration: underline;
}

.advanced-toggle:hover {
  cursor: pointer;
}
</style>
